/**
 * Módulo de autenticación — login, registro y verificación de email via endpoints públicos
 */

/**
 * Crea el módulo de autenticación
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.request - Función de request sin autenticación
 * @returns {Object} Métodos del módulo de autenticación
 */
export function createAuthModule({ request, authenticatedRequest }) {
  return {
    /**
     * Inicia sesión de usuario
     *
     * @param {string} email - Email del usuario
     * @param {string} password - Contraseña del usuario
     * @returns {Promise<{success: boolean, token?: string, user?: Object, message?: string}>}
     */
    async login(email, password) {
      return await request('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
    },

    /**
     * Registra un nuevo usuario
     *
     * @param {Object} userData - Datos del nuevo usuario
     * @param {string} userData.email - Email del usuario
     * @param {string} userData.password - Contraseña (mínimo 6 caracteres)
     * @param {string} userData.nombre - Nombre completo del usuario
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    async register(userData) {
      return await request('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
    },

    /**
     * Verifica código de email enviado durante registro
     *
     * @param {string} email - Email del usuario a verificar
     * @param {string} codigo - Código de 6 dígitos recibido por email
     * @returns {Promise<{success: boolean, token?: string, message?: string}>}
     */
    async verificarCodigo(email, codigo) {
      return await request('/api/auth/verificar-codigo', {
        method: 'POST',
        body: JSON.stringify({ email, codigo })
      });
    },

    /**
     * Reenvía código de verificación al email
     *
     * @param {string} email - Email donde enviar el nuevo código
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    async reenviarCodigo(email) {
      return await request('/api/auth/reenviar-codigo', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
    },

    /**
     * Obtiene perfil del usuario autenticado
     *
     * @returns {Promise<{success: boolean, data: {id: number, email: string, nombre: string}}>}
     */
    async getMiPerfil() {
      return await authenticatedRequest('/api/auth/me');
    }
  };
}
